import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { useAuth } from "../../js/hooks/useAuth";
import { useDesk } from "../../js/hooks/useDesk";
import {
  CONTACT_METHODS,
  CONTACT_REASONS,
  INTEREST_LEVELS,
  canManageProspects,
  canViewProspects,
  convertProspect,
  createProspect,
  ensureProspects,
  getProspect,
  listProspectMessages,
  listProspectTimeline,
  listProspects,
  prospectPortalUrl,
  sendProspectNote,
  setFollowUp,
  updateProspect,
} from "../../js/prospects";
import { formatDate } from "../../js/utils/formatters";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  preferred_contact: "email",
  reason: "general",
  interest: "warm",
  notes: "",
  property_of_interest: "",
};

export default function ProspectsPage() {
  const { session, role } = useAuth();
  const desk = useDesk();
  const { id } = useParams();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [quick, setQuick] = useState("");
  const [copied, setCopied] = useState(false);
  const manage = canManageProspects(session);

  ensureProspects();

  useEffect(() => {
    function onChange() {
      setPulse((n) => n + 1);
    }
    window.addEventListener("dh-prospects-change", onChange);
    return () => window.removeEventListener("dh-prospects-change", onChange);
  }, []);

  const rows = useMemo(() => (canViewProspects(session) ? listProspects(session) : []), [session, pulse]);
  const selected = id && session ? getProspect(id, session) : null;
  const timeline = selected ? listProspectTimeline(selected.id) : [];
  const history = selected ? listProspectMessages(selected) : [];
  const overdue = rows.filter((row) => row.overdue).length;
  const share = session?.userId ? prospectPortalUrl(session.userId) : "";
  const variant = role === "admin" ? "admin" : desk.variant === "broker" ? "broker" : "agent";
  const base = desk.prospects || `${desk.base}/prospects`;

  function field(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function add(event) {
    event.preventDefault();
    setError("");
    setNote("");
    try {
      const row = createProspect(session, form);
      setForm(EMPTY);
      setNote("Prospect added.");
      navigate(`${base}/${row.id}`);
    } catch (err) {
      setError(err.message);
    }
  }

  function saveCard(event) {
    event.preventDefault();
    if (!selected) return;
    setError("");
    try {
      updateProspect(session, selected.id, {
        notes: event.target.notes.value,
        preferred_contact: event.target.preferred_contact.value,
        interest: event.target.interest.value,
        phone: event.target.phone.value,
      });
      setNote("Saved.");
      setPulse((n) => n + 1);
    } catch (err) {
      setError(err.message);
    }
  }

  function follow(event) {
    event.preventDefault();
    if (!selected) return;
    setError("");
    try {
      setFollowUp(session, selected.id, {
        next_contact: event.target.next_contact.value,
        follow_note: event.target.follow_note.value,
        follow_status: event.target.follow_status.value,
      });
      setNote("Follow-up updated.");
      setPulse((n) => n + 1);
    } catch (err) {
      setError(err.message);
    }
  }

  function sendQuick(event) {
    event.preventDefault();
    if (!selected) return;
    setError("");
    try {
      sendProspectNote(session, selected.id, quick);
      setQuick("");
      setNote("Note sent. It also appears in Client Messaging.");
      setPulse((n) => n + 1);
    } catch (err) {
      setError(err.message);
    }
  }

  function convert(kind) {
    if (!selected) return;
    setError("");
    try {
      convertProspect(session, selected.id, kind);
      setNote(`Moved into a ${kind} File. Timeline and notes stay here.`);
      setPulse((n) => n + 1);
    } catch (err) {
      setError(err.message);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(share);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  if (!canViewProspects(session)) {
    return (
      <AppLayout variant="agent">
        <article className="prospects">
          <h1>Prospects</h1>
          <p>Prospects stay on the agent and broker Desk.</p>
        </article>
      </AppLayout>
    );
  }

  return (
    <AppLayout variant={variant}>
      <div className="prospects">
        <header className="page-header">
          <div>
            <p className="eyebrow">Before a File</p>
            <h1>Prospects</h1>
            <p className="page-lead">
              A quiet book for people who are not yet clients. Buyer and seller journeys stay
              apart until you convert.
            </p>
          </div>
        </header>

        {overdue ? (
          <p className="prospects-overdue">
            {overdue} follow-up{overdue === 1 ? "" : "s"} overdue.
          </p>
        ) : null}
        {note ? <p className="confirm-banner">{note}</p> : null}
        {error ? <p className="field-error">{error}</p> : null}

        {manage ? (
          <section className="prospects-share card">
            <p>
              Public door for people who do not have an account. Their note opens a prospect card
              and a thread on your Desk.
            </p>
            <code>{share}</code>
            <button type="button" className="ghost-btn" onClick={copyLink}>
              {copied ? "Copied" : "Copy link"}
            </button>
          </section>
        ) : (
          <p className="muted">You may look. Changes stay with the agent or broker who holds the list.</p>
        )}

        <div className="prospects-grid">
          <aside className="prospects-list card">
            <h2>List</h2>
            <ul>
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`${base}/${row.id}`}
                    className={`prospects-row${id === row.id ? " is-on" : ""}${row.overdue ? " is-late" : ""}`}
                  >
                    <strong>{row.name}</strong>
                    <span>
                      {row.interest_label}
                      {row.status === "converted" ? " · File" : ""}
                      {row.overdue ? " · Overdue" : ""}
                    </span>
                    <em>{row.next_contact || "No follow-up"}</em>
                  </Link>
                </li>
              ))}
              {!rows.length ? <li className="muted">No prospects yet.</li> : null}
            </ul>

            {manage ? (
              <form className="prospects-add" onSubmit={add}>
                <h3>Add a prospect</h3>
                <input className="field-input" name="name" value={form.name} onChange={field} placeholder="Name" required />
                <input className="field-input" name="email" type="email" value={form.email} onChange={field} placeholder="Email" required />
                <input className="field-input" name="phone" value={form.phone} onChange={field} placeholder="Phone" />
                <select className="field-select" name="reason" value={form.reason} onChange={field}>
                  {CONTACT_REASONS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <select className="field-select" name="interest" value={form.interest} onChange={field}>
                  {INTEREST_LEVELS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <button className="primary-btn" type="submit">
                  Add
                </button>
              </form>
            ) : null}
          </aside>

          <section className="prospects-detail card">
            {!selected ? (
              <p className="muted">Choose a prospect to see the card, timeline, and notes.</p>
            ) : (
              <>
                <header className="prospects-detail__head">
                  <div>
                    <h2>{selected.name}</h2>
                    <p className="muted">
                      {selected.email}
                      {selected.phone ? ` · ${selected.phone}` : ""}
                      {` · ${selected.interest_label}`}
                      {selected.status === "converted" ? ` · ${selected.converted_kind} File` : " · Open"}
                    </p>
                  </div>
                  {manage && selected.status === "open" ? (
                    <div className="prospects-convert">
                      <button type="button" className="primary-btn" onClick={() => convert("buyer")}>
                        Convert to buyer
                      </button>
                      <button type="button" className="ghost-btn" onClick={() => convert("seller")}>
                        Convert to seller
                      </button>
                    </div>
                  ) : null}
                </header>

                <p>
                  Preferred contact: {selected.preferred_contact || "email"}
                  {selected.reason_label ? ` · ${selected.reason_label}` : ""}
                  {selected.property_of_interest ? ` · ${selected.property_of_interest}` : ""}
                </p>
                <p className="muted">
                  Last contact {selected.last_contact ? formatDate(selected.last_contact) : "—"}
                  {" · "}
                  Next {selected.next_contact || "—"}
                  {selected.overdue ? " · Overdue" : ""}
                </p>

                {manage ? (
                  <form className="prospects-form" onSubmit={saveCard}>
                    <label className="field">
                      <span className="field-label">Phone</span>
                      <input className="field-input" name="phone" defaultValue={selected.phone} key={`${selected.id}-phone`} />
                    </label>
                    <label className="field">
                      <span className="field-label">Preferred contact</span>
                      <select className="field-select" name="preferred_contact" defaultValue={selected.preferred_contact} key={`${selected.id}-pref`}>
                        {CONTACT_METHODS.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field-label">Interest</span>
                      <select className="field-select" name="interest" defaultValue={selected.interest} key={`${selected.id}-int`}>
                        {INTEREST_LEVELS.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field-label">Notes</span>
                      <textarea className="field-textarea" name="notes" rows={3} defaultValue={selected.notes} key={`${selected.id}-notes`} />
                    </label>
                    <button className="ghost-btn" type="submit">
                      Save card
                    </button>
                  </form>
                ) : (
                  <p>{selected.notes || "No notes yet."}</p>
                )}

                {manage && selected.status === "open" ? (
                  <form className="prospects-form" onSubmit={follow}>
                    <h3>Follow-up</h3>
                    <label className="field">
                      <span className="field-label">Next contact</span>
                      <input className="field-input" type="date" name="next_contact" defaultValue={selected.next_contact} key={`${selected.id}-next`} />
                    </label>
                    <label className="field">
                      <span className="field-label">Follow-up note</span>
                      <input className="field-input" name="follow_note" defaultValue={selected.follow_note} key={`${selected.id}-fn`} />
                    </label>
                    <label className="field">
                      <span className="field-label">Status</span>
                      <select className="field-select" name="follow_status" defaultValue={selected.follow_status || "scheduled"} key={`${selected.id}-fs`}>
                        <option value="scheduled">Scheduled</option>
                        <option value="done">Done</option>
                      </select>
                    </label>
                    <button className="ghost-btn" type="submit">
                      Save follow-up
                    </button>
                  </form>
                ) : null}

                {manage && selected.status === "open" ? (
                  <form className="prospects-form" onSubmit={sendQuick}>
                    <h3>Quick message</h3>
                    <textarea
                      className="field-textarea"
                      rows={3}
                      value={quick}
                      onChange={(event) => setQuick(event.target.value)}
                      placeholder="A short note. It also appears in Client Messaging."
                    />
                    <button className="primary-btn" type="submit">
                      Send
                    </button>
                    {selected.thread_id ? (
                      <p>
                        <Link to={`${desk.messages}?thread=${selected.thread_id}`}>Open in Client Messaging</Link>
                      </p>
                    ) : null}
                  </form>
                ) : null}

                <section>
                  <h3>Communication history</h3>
                  <ol className="prospects-history">
                    {history.map((item) => (
                      <li key={item.id}>
                        <strong>{item.sender_id === selected.user_id ? selected.name : "Desk"}</strong>
                        <span>{formatDate(item.timestamp)}</span>
                        <p>{item.message_text}</p>
                      </li>
                    ))}
                    {!history.length ? <li className="muted">No messages yet.</li> : null}
                  </ol>
                </section>

                <section>
                  <h3>Timeline</h3>
                  <ol className="prospects-history">
                    {timeline.map((item) => (
                      <li key={item.id}>
                        <strong>{item.kind}</strong>
                        <span>{formatDate(item.at)}</span>
                        <p>{item.body}</p>
                      </li>
                    ))}
                  </ol>
                </section>
              </>
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
